// Landing from a password-reset email; one copy in each of SkyWeather, SkyWaiver, SkyBook and MORAD. Change all four.
/**
 * Landing from a password-reset email, shared by the four Stratica staff sign-ins (SkyWeather, SkyWaiver,
 * SkyBook, MORAD). The identity client runs with `detectSessionInUrl: false`, so nothing reads the link on its
 * own: the app reads the hash once, signs in from the tokens in it, asks for a new password, and clears the hash.
 *
 * A reset link comes back to the app's address as
 *   #access_token=...&refresh_token=...&expires_in=3600&token_type=bearer&type=recovery
 * and, when it is expired or already used, as
 *   #error=access_denied&error_code=otp_expired&error_description=...
 * Same parsing as Ops (SkyDriveHQ/skydrivehq code/packages/ops/src/supabase/auth-redirect.ts).
 */

export type RecoveryLanding =
  | { readonly kind: 'recovery'; readonly accessToken: string; readonly refreshToken: string }
  | { readonly kind: 'expired' }
  | null;

/** What the address hash says about a reset link. Null for any other hash (a kiosk key, an anchor, nothing). */
export function parseRecoveryHash(hash: string): RecoveryLanding {
  const h = hash.replace(/^#\/?/, '');
  if (!h.includes('type=recovery') && !h.includes('error=')) return null;
  const p = new URLSearchParams(h);
  if (p.get('error')) return { kind: 'expired' };
  if (p.get('type') !== 'recovery') return null;
  const accessToken = p.get('access_token');
  const refreshToken = p.get('refresh_token');
  if (!accessToken || !refreshToken) return null;
  return { kind: 'recovery', accessToken, refreshToken };
}

/** Drop the tokens from the address bar and history: keeps the path and query, loses the hash. */
export function clearAuthHash(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    // A sandboxed frame may refuse; the tokens are single-use anyway.
  }
}

/** What the address said when the page loaded, read once so a re-render or StrictMode cannot lose it. */
export const initialLanding: RecoveryLanding = typeof window === 'undefined' ? null : parseRecoveryHash(window.location.hash);
